import React, { useEffect, useMemo, useRef, useState } from 'react';

import chunk from 'lodash-es/chunk';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { MultiSelect, MultiSelectChangeParams } from 'primereact/multiselect';
import useDashboardStore from 'store/dashboard-store';
import useDatasetIdentifiersStore from 'store/dataset-identifiers-store';
import useScadaStore from 'store/scada-store';
import useSelectedScadaTagsStore from 'store/selected-scada-tags-store';
import CalendarWrapper from '../../basic/calendar-wrapper/CalendarWrapper';
import './Scada.scss';
import ScadaTags from './components/ScadaTags';
import { ScadaProps, ScadaTagsProps } from './scadaTypes';
import { defaultDateTime, defaultElementParameters } from './utils/default-values';
import { getScadaImageUrl, showCords, tempId } from './utils/scadaUtils';

const CHUNK_SIZE = 16;

export default function Scada(props: ScadaProps) {
	const [parent, setParent] = useState(defaultElementParameters);
	const [dateTime, setDateTime] = useState(defaultDateTime);
	const [layers, setLayers] = useState<string[]>([]);
	const [gridSize, setGridSize] = useState(0.5);
	const [labelPosition, setLabelPosition] = useState({x: 0, y: 0});
	const isEditMode = useDashboardStore(state => state.isEditMode);
	
	const ref = useRef<HTMLDivElement>(null);
	const scadaStore = useScadaStore();
	const selectedIdsStore = useDatasetIdentifiersStore();
	const selectedTagsStore = useSelectedScadaTagsStore();

	const selectedTags = selectedTagsStore.tags;

	const scada = scadaStore.scadaMap.get(props.id ?? tempId(props));
	let timeout: NodeJS.Timeout | null = null;
	const layerOptions = useMemo(() => scada?.labelLayers ?? [], [scada?.labelLayers]);

	useEffect(() => {
		if (!scada) {
			scadaStore.addScada({
				scadaId: props.id ?? tempId(props),
				labels: props.elements ?? [],
				labelLayers: props.labelLayers ?? [],
			});
		} else {
			scadaStore.updateScadaLabels(props.id!, props.elements ?? []);
		}
	}, []);

	useEffect(() => {
		const rect = ref.current!.getBoundingClientRect();
		setParent({
			width: rect.width,
			height: rect.height,
			top: rect.top,
			left: rect.left,
		});
	}, [ref.current]);

	useEffect(() => {
		setLayers(layerOptions.filter(g => g.visible).map(g => g.layer));
	}, [layerOptions])

	useEffect(() => {
		if(selectedTags.length > 0 && selectedTags[0].dataset.x && selectedTags[0].dataset.y) {
			setLabelPosition({
        x: Number(selectedTags[0]?.dataset.x),
        y: Number(selectedTags[0]?.dataset.y),
      });
		}
	}, [selectedTags])

	const handleScroll = () => {
		if (timeout) {
			clearTimeout(timeout);
		}
		timeout = setTimeout(() => {
			const rect = ref.current!.getBoundingClientRect();
			setParent({
				width: rect.width,
				height: rect.height,
				top: rect.top,
				left: rect.left,
			});
		}, 200);
	};

	useEffect(() => {
		window.addEventListener('scroll', handleScroll);

		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	const handleLayerMultiselect = (e: MultiSelectChangeParams) => {
		const updLayers = layerOptions.map(l => ({
			...l,
			visible: e.value.includes(l.layer),
		}));
		scadaStore.updateScadaLabelLayers(props.id!, updLayers);
		setLayers(e.value);
	};

	const handleCoordChange = (x: number, y: number) => {
		if (selectedTags.length === 1) {
			setLabelPosition({ x, y });
		}
	};

	const elemProps: ScadaTagsProps = {
		...props,
		date: dateTime.manualTime ? dateTime.date : undefined,
		returnDate: e => setDateTime(prev => ({ ...prev, date: e })),
		parent,
		layerOptions,
		gridSize,
		onCoordChange: handleCoordChange,
	};

	const cancelSelections = (e: React.MouseEvent) => {
    if (e.ctrlKey) return;

    if (new Date().getTime() - scadaStore.lastClick < 500) return;

		selectedTags.forEach(el => el.children[0].classList.remove("selectedLabel"));
    selectedTagsStore.clear();
		selectedIdsStore.clear();
	};

	const chunkedElements = chunk(props.elements, CHUNK_SIZE);
	return (
      <div className="flex flex-column gap-3">
        <div className="flex justify-content-between">
          {props.showScadaSettings && (
            <div className="flex gap-1">
              <CalendarWrapper
                value={dateTime.date}
                onChange={e => setDateTime({ date: e, manualTime: true })}
              />
              {dateTime.manualTime && (
                <Button
                  icon="pi pi-sync"
                  disabled={!dateTime.manualTime}
                  onClick={() => setDateTime(defaultDateTime)}
                />
              )}
            </div>
          )}
          <div className="flex gap-5 justify-content-end w-full">
            {isEditMode && (
              <div className="flex gap-5 align-items-baseline">
                {selectedTags.length === 1 && selectedTags[0]?.dataset.scadaId === props.id && (
                  <span className="border-1 border-gray-300 border-round scada-p-9px">
                    {`x: ${labelPosition.x.toFixed(1)}%, y: ${labelPosition.y.toFixed(1)}%`}
                  </span>
                )}
                <div className="flex gap-2 align-items-center">
                  <label htmlFor="gridSize">Grid, %:</label>
                  <InputNumber
                    inputId="gridSize"
                    value={gridSize}
                    onChange={e => setGridSize(e.value ?? 1)}
                    inputStyle={{ width: "3rem" }}
                    maxFractionDigits={1}
                  />
                </div>
              </div>
            )}

            {layerOptions.length > 1 && (
              <MultiSelect
                value={layers}
                onChange={handleLayerMultiselect}
                options={layerOptions.map(g => g.layer)}
                className="w-8rem"
                maxSelectedLabels={0}
                selectedItemsLabel="Layers"
                placeholder="Layers"
              />
            )}
          </div>
        </div>
        <div
          ref={ref}
          className="scadaViewContainer relative"
          onClick={cancelSelections}
          onMouseMove={e => showCords(e, isEditMode)}
        >
          <img
            alt="SCADA schematics"
            className="scadaView"
            src={getScadaImageUrl(props.scadaBackgroundId)}
          />
          {chunkedElements.map(elements => (
            <ScadaTags key={elements.map(e => e.identifier).toString()} {...elemProps} elements={elements} />
          ))}
        </div>
      </div>
  );
}

